import { Box, Button, Container, Header, Modal } from '@amzn/awsui-components-console';
import { get as _get } from 'lodash';
import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';

import { IngressTable } from '@/components';
import { CLASS_ACCESS_TYPES } from '@/components/classForm/ClassForm.utils';
import { ClassRosterTable } from '@/components/ClassRosterTable';
import { LabPoolDetails } from '@/components/pooledLabs/labPoolDetails';
import { isTrainingAnActivePooledLab } from '@/components/pooledLabs/labPoolDetails/utils';
import { CreateProvisionPoolModal } from '@/components/pooledLabs/provisionPoolModal';
import { useClassroomData } from '@/data/useClassroomData';
import { useLabRegionSelection } from '@/data/useLabRegionSelection';
import { getStudentTrainingsByClassroomId } from '@/graphql/queries';
import { executeRequest } from '@/modules/api';
import { removeCourseNameFromLabTitle, useFlags } from '@/utils';
import { useDecodedParams, useParseUrlParamsForLabData } from '@/utils/paramHooks';
import { PooledLabsProvider } from '@/utils/pooledLabs';

import messages from './Lab.messages';

const errorMessages = {
    'getStudentTrainingsByClassroomId::403': messages.getStudentTrainingsByClassroomId403Error,
    default: messages.cannotGetStudentLabs,
};

const A_MINUTE = 60000;

const useQueryOptions = {
    refetchOnWindowFocus: false,
    refetchInterval: A_MINUTE,
};

const Lab = () => {
    const { supportsActivities } = useFlags();
    const { classroomId } = useDecodedParams();
    const { contentId: labId, title: labTitle } = useParseUrlParamsForLabData();
    const { formatMessage } = useIntl();
    const { setRegion, selectedRegion } = useLabRegionSelection();
    const { classData, refetch: refetchClassroom, hasNoLabs } = useClassroomData(classroomId);
    const { classroom } = classData;

    const [getTrainingsError, getTrainingsErrorSet] = useState(false);
    const hasStudentRoster = classroom?.accessType === CLASS_ACCESS_TYPES.roster;

    const queryParams = {
        classroomId,
        providerArn: classroom?.providerArn,
        accessType: classroom?.accessType,
    };
    if (hasStudentRoster) {
        queryParams.accessType = CLASS_ACCESS_TYPES.roster;
    }

    const { data, isLoading, isFetching, refetch, error } = useQuery(
        [classroomId, queryParams],
        () =>
            executeRequest({
                operation: getStudentTrainingsByClassroomId,
                params: queryParams,
            }),
        {
            ...useQueryOptions,
            enabled: !!classData?.classroom,
        },
    );

    const onRefetch = () => Promise.all([refetch(), refetchClassroom()]);

    useEffect(() => {
        if (error) {
            const msg =
                errorMessages[`${error.path}::${error.statusCode}`] || errorMessages.default;
            getTrainingsErrorSet(msg);
        }
    }, [error]);

    /**
     * Currently we are getting all training from the activity service.
     * This has some complications.
     *
     * 1. A training record can reference old lab arns
     * 2. A training record may be in an error state
     * 3. We do not keep state, each student has more than one "active" lab, this is because
     * we are not onboarded to the labs hook notification. https://sim.amazon.com/issues/BKR-2236
     */

    const studentTrainingsByClass = data?.getStudentTrainingsByClassroomId.studentTrainings ?? [];
    const studentTrainingsByCurrentLabId = studentTrainingsByClass.filter(
        ({ arn }) => arn === labId,
    );
    const activePooledLabsCount = studentTrainingsByClass.reduce(
        (acc, training) => (isTrainingAnActivePooledLab(training) ? acc + 1 : acc),
        0,
    );

    const courseTitle = classData?.course?.title;

    return (
        <PooledLabsProvider
            classroomId={classroomId}
            providerArn={classroom?.providerArn}
            langLocale={classroom?.langLocale}
            studentTrainings={studentTrainingsByCurrentLabId}
            labId={labId}
            batchRegion={selectedRegion}
        >
            {supportsActivities ? <ClassRosterTable /> : null}
            {hasNoLabs ? null : (
                <Container
                    header={
                        <Header
                            variant='h2'
                            actions={
                                <CreateProvisionPoolModal
                                    selectedRegion={selectedRegion}
                                    activePooledLabsCount={activePooledLabsCount}
                                />
                            }
                            description={formatMessage(messages.sectionSubheader)}
                        >
                            {removeCourseNameFromLabTitle(labTitle, courseTitle)}
                        </Header>
                    }
                >
                    <LabPoolDetails
                        trainings={studentTrainingsByCurrentLabId}
                        activePooledLabsCount={activePooledLabsCount}
                        selectedRegion={selectedRegion}
                    />
                    <IngressTable
                        labId={labId}
                        loading={isLoading}
                        langLocale={classroom?.langLocale}
                        studentTrainings={studentTrainingsByClass}
                        trainingRefetch={onRefetch}
                        trainingLoading={isFetching}
                        hasStudentRoster={hasStudentRoster}
                        setRegion={setRegion}
                        selectedRegion={selectedRegion}
                    />
                </Container>
            )}

            {getTrainingsError ? (
                <Modal
                    visible
                    onDismiss={() => getTrainingsErrorSet(false)}
                    header={formatMessage(messages.somethingWentWrongTrainings)}
                    closeAriaLabel={formatMessage(messages.closeModalLabelText)}
                    footer={
                        <Box float='right'>
                            <Button
                                onClick={() => getTrainingsErrorSet(false)}
                                variant='primary'
                                data-testid='lab-training-errors-modal__close-btn'
                            >
                                {formatMessage(messages.close)}
                            </Button>
                        </Box>
                    }
                >
                    {formatMessage(getTrainingsError)}
                </Modal>
            ) : null}
        </PooledLabsProvider>
    );
};

export default Lab;
